import { MealSlot, MealType } from "./api/client";

export const DAYS = ["Dl", "Dm", "Dc", "Dj", "Dv", "Ds", "Dg"];

export const MEAL_TYPE_LABELS: Record<MealType, string> = {
  breakfast: "Esmorzar",
  lunch: "Dinar",
  snack: "Snack",
  subrecipe: "Subrecepta",
};

export const SLOT_ROWS: { slot: MealSlot; label: string }[] = [
  { slot: "breakfast1", label: "Esmorzar" },
  // El segon esmorzar ("Esmorzar Fora") queda amagat de moment. El backend
  // encara crea la franja breakfast2, així que per recuperar-lo n'hi ha prou
  // de descomentar aquesta línia.
  // { slot: "breakfast2", label: "Esmorzar Fora" },
  { slot: "lunch", label: "Dinar" },
  { slot: "snack", label: "Snacks" },
  { slot: "dinner", label: "Sopar" },
];

/**
 * Quins tipus de recepta accepta cada franja horària.
 * - breakfast i snack es poden posar als 2 esmorzars i al berenar
 * - lunch i snack als slots de dinar i sopar
 * - breakfast queda explícitament fora de dinar/sopar
 */
export const SLOT_ALLOWED_TYPES: Record<MealSlot, MealType[]> = {
  breakfast1: ["breakfast", "snack"],
  breakfast2: ["breakfast", "snack"],
  snack: ["breakfast", "snack"],
  lunch: ["lunch", "snack"],
  dinner: ["lunch", "snack"],
};

export function isSlotCompatible(slot: MealSlot, mealType: MealType): boolean {
  return SLOT_ALLOWED_TYPES[slot].includes(mealType);
}

export const DAY_LABELS_FULL: Record<number, string> = {
  1: "Dilluns",
  2: "Dimarts",
  3: "Dimecres",
  4: "Dijous",
  5: "Divendres",
  6: "Dissabte",
  7: "Diumenge",
};

/**
 * Estat efectiu de l'usuari per a un (dia, slot):
 * default ± excepció (la presència d'una excepció inverteix el default).
 */
export function isUserActiveOnSlot(
  slotDefaults: MealSlot[],
  slotExceptions: { day: number; mealSlot: MealSlot }[],
  day: number,
  slot: MealSlot
): boolean {
  const inDefault = slotDefaults.includes(slot);
  const flipped = slotExceptions.some((e) => e.day === day && e.mealSlot === slot);
  return flipped ? !inDefault : inDefault;
}

/** Dilluns de la setmana ISO de la data donada, format YYYY-MM-DD (UTC). */
export function isoMondayString(d: Date = new Date()): string {
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = utc.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  utc.setUTCDate(utc.getUTCDate() + diff);
  return utc.toISOString().slice(0, 10);
}

export function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const MONTHS_CA = [
  "gener",
  "febrer",
  "març",
  "abril",
  "maig",
  "juny",
  "juliol",
  "agost",
  "setembre",
  "octubre",
  "novembre",
  "desembre",
];

export function formatRange(monday: string): string {
  const r = formatWeekRange(monday);
  return r.label;
}

export function formatWeekRange(monday: string): { label: string; year: string } {
  const start = new Date(monday + "T00:00:00Z");
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);

  const sd = start.getUTCDate();
  const ed = end.getUTCDate();
  const sm = start.getUTCMonth();
  const em = end.getUTCMonth();
  const sy = start.getUTCFullYear();
  const ey = end.getUTCFullYear();

  const sMonth = MONTHS_CA[sm];
  const eMonth = MONTHS_CA[em];

  const label =
    sm === em && sy === ey
      ? `del ${sd} al ${ed} de ${sMonth}`
      : `del ${sd} de ${sMonth} al ${ed} de ${eMonth}`;
  const year = sy === ey ? String(sy) : `${sy}–${ey}`;
  return { label, year };
}

/**
 * Tradueix unitats d'ingredient angleses al català.
 *   tsp  → cullereta / culleretes (cullerada petita)
 *   tbsp → cullerada / cullerades (cullerada gran)
 *   u    → unitat / unitats
 * La resta queda igual (g, ml, kg, etc.).
 */
export function formatUnit(unit: string, quantity: number): string {
  const u = unit.trim().toLowerCase();
  const plural = quantity !== 1;

  if (u === "tsp" || u === "teaspoon" || u === "teaspoons") {
    return plural ? "culleretes" : "cullereta";
  }
  if (u === "tbsp" || u === "tablespoon" || u === "tablespoons") {
    return plural ? "cullerades" : "cullerada";
  }
  if (u === "u" || u === "unitat" || u === "unitats" || u === "ud" || u === "uds") {
    return plural ? "unitats" : "unitat";
  }
  return unit;
}

/**
 * Categories de supermercat (zones). Ordre pensat per recórrer la botiga i
 * etiquetes compartides entre l'editor d'ingredients i la llista de la compra.
 */
export const SHOP_ZONE_ORDER = [
  "verdura", "carn", "peix", "pesca_salada", "xarcuteria",
  "lactics", "congelats", "fleca", "basar", "conserves",
  "olis", "plats_preparats", "aigues", "refrescos", "sucs",
  "cerveses", "celler", "neteja", "altres",
] as const;

export const SHOP_ZONE_LABELS: Record<string, string> = {
  verdura: "Verdura i fruita",
  carn: "Carn i aviram",
  peix: "Peix i marisc",
  pesca_salada: "Pesca Salada",
  xarcuteria: "Xarcuteria i formatge",
  lactics: "Làctics i ous",
  congelats: "Congelats",
  fleca: "Fleca",
  basar: "Basar (pasta, arròs, llegums…)",
  conserves: "Conserves",
  olis: "Olis i condiments",
  plats_preparats: "Plats preparats",
  aigues: "Aigües",
  refrescos: "Refrescos",
  sucs: "Sucs",
  cerveses: "Cerveses",
  celler: "Celler",
  neteja: "Neteja i drogueria",
  altres: "Altres",
};

/** Etiqueta del grup per a articles sense categoria assignada. */
export const SHOP_ZONE_UNASSIGNED_LABEL = "Sense categoria";

/** Ordena (zona → índex) per agrupar; les no assignades van al final. */
export function shopZoneRank(zone: string | null | undefined): number {
  if (!zone) return SHOP_ZONE_ORDER.length;
  const idx = (SHOP_ZONE_ORDER as readonly string[]).indexOf(zone);
  return idx === -1 ? SHOP_ZONE_ORDER.length : idx;
}

/** Unitats predefinides al desplegable de l'editor de receptes. */
export const INGREDIENT_UNITS: { value: string; label: string }[] = [
  { value: "u", label: "unitat" },
  { value: "g", label: "g" },
  { value: "kg", label: "kg" },
  { value: "ml", label: "ml" },
  { value: "l", label: "l" },
  { value: "tsp", label: "cullereta (tsp)" },
  { value: "tbsp", label: "cullerada (tbsp)" },
  { value: "mica", label: "mica" },
];
